import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { faChevronRight, faList } from '@fortawesome/free-solid-svg-icons';
import { FidelityCardTypeEnum } from '../../services/fidelity/fidelity';
import { Subscribable } from '../../stores/tunnel/model/subscribable';
import { TunnelDataStore } from '../../stores/tunnel/tunnel-data.store';
import {
  Funding,
  FundingState,
  InstallationState,
  KitchenExtraPricing,
  Price,
  PricingState,
} from '../../stores/tunnel/model/tunnel-data-state';
import { DiscountTypeEnum } from '../../enum/discount-type.enum';
import { CustomerService } from '../../services/customer.service';
import { CONFIG_CONTEXT } from '../../enum/config-context.enum';
import { KitchenService } from '../../services/configurators-2d/applications/kitchen.service';
import { StorageService } from '../../services/configurators-2d/applications/storage.service';
import { BathroomService } from 'src/app/services/configurators-2d/applications/bathroom.service';
import { BathroomHbmService } from 'src/app/services/configurators-2d/applications/bathroom-hbm.service';
import { CarpentryService } from 'src/app/services/configurators-2d/applications/carpentry.service';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';
import { getLatestCarpentryOrder } from 'src/app/services/helpers/carpentry-order-state.helper';

@Component({
  selector: 'ng-casto-project-info',
  templateUrl: './project-info.component.html',
})
export class ProjectInfoComponent extends Subscribable implements OnInit, OnDestroy {
  protected projectInfoVisible = false;
  protected readonly faList = faList;
  protected readonly faChevronRight = faChevronRight;
  protected readonly FidelityCardTypeEnum = FidelityCardTypeEnum;

  protected withInstallationSwitchValue: boolean = false;
  protected moreThan2YearsSwitchValue: boolean = false;

  protected installationState: InstallationState;

  protected currentPricing: PricingState;
  protected currentDiscount: DiscountTypeEnum;
  protected currentFunding: FundingState;
  protected loading: boolean = false;

  constructor(
    private readonly tunnelDataStore: TunnelDataStore,
    protected customerService: CustomerService,
    private readonly injector: Injector,
    protected featureSettingsService: FeatureSettingsService,
    private readonly carpentryService: CarpentryService
  ) {
    super();
  }

  ngOnInit(): void {
    super.subscribe();
  }

  ngOnDestroy(): void {
    super.unsubscribe();
  }

  get totalExtraPricing(): Price {
    switch (this.customerService.context) {
      case CONFIG_CONTEXT.KITCHEN:
        return (this.currentPricing?.extraPricing as KitchenExtraPricing)?.totalWorkbench;
      default:
        return undefined;
    }
  }

  get discountNumberValue(): number {
    switch (this.currentDiscount) {
      case DiscountTypeEnum.Discount0:
        return 0;
      case DiscountTypeEnum.Discount5:
        return 5;
      case DiscountTypeEnum.Discount10:
        return 10;
      case DiscountTypeEnum.Discount20:
        return 20;
      default:
        return 0;
    }
  }

  get currentFundingValue(): Funding {
    if (!this.featureSettingsService.financementEnabled) {
      return undefined;
    }

    if (!this.currentFunding) {
      return undefined;
    }

    return this.currentFunding[
      this.withInstallationSwitchValue ? 'fundingWithInstallation' : 'fundingWithoutInstallation'
    ] as Funding;
  }

  /*
   * Désactivé volontairement pour le moment dans "Mon projet étape par étape".
   * Ce getter récupérait la dernière commande menuiserie pour afficher des informations
   * comme le numéro de commande Castorama ou le numéro d'opportunité.
   */
  // get currentCarpentryOrder(): ReturnType<typeof getLatestCarpentryOrder> {
  //   if (this.customerService.context !== CONFIG_CONTEXT.CARPENTRY) {
  //     return undefined;
  //   }
  //
  //   return getLatestCarpentryOrder(this.carpentryService.configuration);
  // }

  /*
   * Désactivé volontairement pour le moment dans "Mon projet étape par étape".
   * Ce getter permettait d'afficher le bloc d'informations de commande menuiserie
   * lorsqu'un orderNumber Castorama ou un opportunityNumber étaient disponibles.
   */
  // get hasCarpentryOrderInformation(): boolean {
  //   const order = this.currentCarpentryOrder;
  //
  //   return !!(order?.orderNumber || order?.opportunityNumber);
  // }

  get isInstallationDisabled(): boolean {
    switch (this.customerService.context) {
      case CONFIG_CONTEXT.BATHROOM:
        return this.injector.get(BathroomHbmService).isInstallationDisabled();
      case CONFIG_CONTEXT.BATHROOM_V2:
        return this.injector.get(BathroomService).isInstallationDisabled();
      case CONFIG_CONTEXT.CARPENTRY:
        return this.injector.get(CarpentryService).isInstallationDisabled();
    }

    return false;
  }

  protected subscribeToObservables(): void {
    this.subscriptions.push(
      this.tunnelDataStore.getCurrentInstallation().subscribe(installation => {
        if (installation) {
          this.installationState = installation;
          this.withInstallationSwitchValue = installation.withInstallation;
        } else {
          this.withInstallationSwitchValue = false;
        }
      }),
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),
      this.tunnelDataStore.getCurrentDiscount().subscribe(discount => {
        if (discount) {
          this.currentDiscount = discount;
        }
      }),
      this.tunnelDataStore.getLoading().subscribe(loading => {
        this.loading = loading;
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(funding => {
        if (funding) {
          this.currentFunding = funding;
        }
      })
    );
  }

  protected async updateServiceWithInstallation(withInstallation: boolean): Promise<void> {
    if (!this.featureSettingsService.poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    // Chaque contexte doit persister le choix côté API : sans son `case`, le switch ne
    // modifiait que l'état local et le scénario de pose repartait à sa valeur serveur.
    switch (this.customerService.context) {
      case CONFIG_CONTEXT.KITCHEN:
        await this.injector.get(KitchenService).updateWithInstallation(withInstallation);
        break;
      case CONFIG_CONTEXT.STORAGE:
        await this.injector.get(StorageService).updateWithInstallation(withInstallation);
        break;
      case CONFIG_CONTEXT.BATHROOM:
        await this.injector.get(BathroomHbmService).updateWithInstallation(withInstallation);
        break;
      case CONFIG_CONTEXT.BATHROOM_V2:
        await this.injector.get(BathroomService).updateWithInstallation(withInstallation);
        break;
      case CONFIG_CONTEXT.CARPENTRY:
        await this.injector.get(CarpentryService).updateWithInstallation(withInstallation);
        break;
    }
  }

  protected async setWithInstallation(checked: boolean): Promise<void> {
    if (!this.featureSettingsService.poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    await this.updateServiceWithInstallation(checked);
    this.tunnelDataStore.setWithInstallation(checked);
    this.tunnelDataStore.resetCurrentFunding();
  }
}
